import { useEffect, useState, type FormEvent } from "react";
import { Button, FormRow, Modal, ModalFooter, Notice, SelectField, TextField, useToast } from "@plataforma/design-system";
import type { Appointment, AppointmentWarning, Client } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { errorMessage } from "../../lib/api";
import { ClientPicker, type ClientChoice } from "../clients/ClientPicker";
import { checkAppointment, useLocations, useSaveAppointment } from "./api";
import "./agenda.css";

interface Props {
  appointment?: Appointment;
  defaults?: { date?: string; time?: string; client?: Client };
  onClose: () => void;
  onSaved: (date: string) => void;
}

export function AppointmentForm({ appointment, defaults, onClose, onSaved }: Props) {
  const { config } = useAppConfig();
  const toast = useToast();
  const locations = useLocations();
  const save = useSaveAppointment(appointment?.id);
  const [client, setClient] = useState<ClientChoice>(defaults?.client ? { kind: "existing", client: defaults.client } : { kind: "none" });
  const [values, setValues] = useState({
    type: appointment?.type ?? config.settings.appointmentTypes[0] ?? "",
    locationId: appointment?.locationId ?? "",
    date: appointment?.date ?? defaults?.date ?? "",
    time: appointment?.time ?? defaults?.time ?? "09:00",
    durationMinutes: String(appointment?.durationMinutes ?? 60),
    value: appointment ? String(appointment.value) : "",
  });
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<AppointmentWarning[]>([]);
  const locationId = values.locationId || locations.data?.[0]?.id || "";
  const set = (field: keyof typeof values) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
    setDirty(true);
  };

  // Avisos de conflito e disponibilidade aparecem enquanto a pessoa escolhe; nunca impedem salvar.
  useEffect(() => {
    const complete = Boolean(locationId && values.date && values.time) && Number(values.durationMinutes) >= 10;
    let active = true;
    const timer = setTimeout(() => {
      if (!complete) return setWarnings([]);
      checkAppointment({ appointmentId: appointment?.id, locationId, date: values.date, time: values.time, durationMinutes: Number(values.durationMinutes) }).then((result) => active && setWarnings(result), () => active && setWarnings([]));
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [appointment?.id, locationId, values.date, values.time, values.durationMinutes]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setClientError(null);
    const common = { type: values.type, locationId, date: values.date, time: values.time, durationMinutes: Number(values.durationMinutes), value: Number(values.value) };
    if (!appointment && client.kind === "none") return setClientError("Escolha um cadastro ou crie um novo.");
    if (!appointment && client.kind === "new" && (!client.name.trim() || client.phone.replace(/\D/g, "").length < 8)) return setClientError("Informe nome e telefone.");
    try {
      const input = appointment ? common : client.kind === "existing" ? { ...common, clientId: client.client.id } : client.kind === "new" ? { ...common, newClient: { name: client.name, phone: client.phone } } : common;
      const saved = await save.mutateAsync(input as never);
      toast(saved.warnings.length ? `Consulta salva. Atenção: ${saved.warnings.map((warning) => warning.message).join(" ")}` : appointment ? "Consulta atualizada." : "Consulta marcada.");
      onSaved(saved.appointment.date);
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <Modal title={appointment ? "Editar ou remarcar consulta" : "Marcar consulta"} onClose={onClose} dirty={dirty} wide>
      <form onSubmit={submit}>
        {appointment ? (
          <p style={{ marginBottom: 12 }}><strong>{appointment.clientName}</strong> <span className="ds-muted">· {appointment.clientPhone}</span></p>
        ) : (
          <ClientPicker value={client} onChange={(choice) => { setClient(choice); setDirty(true); setClientError(null); }} error={clientError} />
        )}
        <FormRow>
          <SelectField label="Atendimento" value={values.type} onChange={set("type")}>
            {config.settings.appointmentTypes.map((type) => <option key={type}>{type}</option>)}
          </SelectField>
          <SelectField label="Local" value={locationId} onChange={set("locationId")}>
            {(locations.data ?? []).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </SelectField>
        </FormRow>
        <FormRow>
          <TextField label="Data" type="date" required value={values.date} onChange={set("date")} />
          <TextField label="Horário combinado" type="time" required value={values.time} onChange={set("time")} />
        </FormRow>
        <FormRow>
          <div>
            <TextField label="Duração (minutos)" type="number" min={10} max={360} step={5} required value={values.durationMinutes} onChange={set("durationMinutes")} />
            <div className="agenda-chips">
              {[30, 45, 60, 90].map((minutes) => <button key={minutes} type="button" className="agenda-chip" onClick={() => set("durationMinutes")({ target: { value: String(minutes) } })}>{minutes} min</button>)}
            </div>
          </div>
          <TextField label="Valor combinado" type="number" min={0} step="0.01" inputMode="decimal" required value={values.value} onChange={set("value")} hint="Informe 0 para atendimento sem cobrança." />
        </FormRow>
        {warnings.length ? <Notice>{warnings.map((warning) => warning.message).join(" ")} Você ainda pode salvar se esse horário foi combinado.</Notice> : null}
        {error ? <p className="ds-field__error" role="alert" style={{ marginTop: 10 }}>{error}</p> : null}
        <ModalFooter>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>{appointment ? "Salvar alterações" : "Marcar consulta"}</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
